import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { FolderSystem } from "@/components/features/settings/defaults/matter-file-repository-structures/FolderSystem";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  MultiselectFormField,
  TextAreaFormField,
  TextFormField,
  Text,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateDefaultMatterFileRepositoryStructure,
  useDeleteDefaultMatterFileRepositoryStructure,
  useUpdateDefaultMatterFileRepositoryStructure,
} from "@/lib/data/react-query/matters/matter-default-file-repository-structure";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { DefaultMatterFileRepositoryStructureSchema } from "@/lib/data/schemas/matters/MatterSchema";

import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type DefaultMatterFileRepositoryStructure } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const MATTER_FILE_REPOSITORY_STRUCTURE_FORM_ID =
  "matter-file-repository-structure-editor-form";

const modalFieldsetClassName =
  "relative grid min-h-0 grid-cols-1 gap-4 sm:grid-cols-2";

interface AddOrEditMatterFileRepositoryStructureModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterFileRepositoryStructure: DefaultMatterFileRepositoryStructure;
      setActiveMatterFileRepositoryStructureId: (id: string) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterFileRepositoryStructure?: never;
      setActiveMatterFileRepositoryStructureId?: never;
    };

const formSchema = DefaultMatterFileRepositoryStructureSchema.pick({
  label: true,
  description: true,
}).extend({
  incidentTypeIds: z.array(z.string()).min(1, {
    message: "Select at least one incident type.",
  }),
});

const defaultFormValues = {
  label: "",
  description: "",
  incidentTypeIds: [],
};

export function AddOrEditMatterFileRepositoryStructureModal({
  open,
  setOpen,
  activeMatterFileRepositoryStructure,
  editing,
  isReadOnly,
}: AddOrEditMatterFileRepositoryStructureModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...activeMatterFileRepositoryStructure,
          incidentTypeIds:
            activeMatterFileRepositoryStructure.incidentTypeIds ?? [],
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const incidentTypes = matterSettingsData?.data.incidentTypes ?? [];
  const createDefaultMatterFileRepositoryStructure =
    useCreateDefaultMatterFileRepositoryStructure();
  const editDefaultMatterFileRepositoryStructure =
    useUpdateDefaultMatterFileRepositoryStructure();
  const deleteDefaultMatterFileRepositoryStructure =
    useDeleteDefaultMatterFileRepositoryStructure();
  const isFormLoading =
    createDefaultMatterFileRepositoryStructure.isPending ||
    editDefaultMatterFileRepositoryStructure.isPending ||
    deleteDefaultMatterFileRepositoryStructure.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const incidentTypesToCreate = incidentTypes
      .filter((incidentType) => data.incidentTypeIds.includes(incidentType.id))
      .map((incidentType) => incidentType.id);
    if (editing) {
      await editDefaultMatterFileRepositoryStructure.mutateAsync({
        id: activeMatterFileRepositoryStructure.id,
        ...data,
        incidentTypeIds: incidentTypesToCreate,
        dateModified: activeMatterFileRepositoryStructure.dateModified,
      });
    } else {
      await createDefaultMatterFileRepositoryStructure.mutateAsync({
        ...data,
        incidentTypeIds: incidentTypesToCreate,
      });
    }
    setOpen(false);
    form.reset();
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteDefaultMatterFileRepositoryStructure
        .mutateAsync({
          id: activeMatterFileRepositoryStructure.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const modalTitle = useMemo(
    () =>
      isReadOnly
        ? "View folder structure"
        : editing
          ? "Edit folder structure"
          : "Create folder structure",
    [isReadOnly, editing],
  );

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={modalTitle}
          size="3xl"
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-1 flex-wrap gap-2">
                {!isReadOnly &&
                editing &&
                hasActionPermission(
                  ACTION_PERMISSION_KEYS.defaultMatterFileRepositoryStructure,
                  ACTION_PERMISSION_VALUES.delete,
                ) ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Delete
                  </Button>
                ) : null}
              </div>
              <div className="flex justify-end gap-x-2 sm:ml-auto">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly ? (
                  <FormSubmitButton
                    submitFormId={MATTER_FILE_REPOSITORY_STRUCTURE_FORM_ID}
                    title="Save"
                    variant="foreground"
                  />
                ) : null}
              </div>
            </div>
          }
        >
          <form
            id={MATTER_FILE_REPOSITORY_STRUCTURE_FORM_ID}
            className="relative flex min-h-0 flex-1 flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {isFormLoading ? <FormLoadingOverlay /> : null}
            <fieldset disabled={isReadOnly} className={modalFieldsetClassName}>
              <div className="col-span-1 sm:col-span-2">
                <TextFormField
                  register={form.register("label")}
                  label="Structure name*"
                  dataTestId="matter-file-repository-structure-label-input"
                />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <TextAreaFormField
                  register={form.register("description")}
                  label="Description"
                />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <MultiselectFormField
                  label="Incident types*"
                  placeholder="Select incident types"
                  showChips
                  options={incidentTypes.map((incidentType) => ({
                    label: getIncidentTypeLabel(incidentType),
                    value: incidentType.id,
                  }))}
                  register={form.register("incidentTypeIds")}
                />
              </div>
            </fieldset>
            {editing ? (
              <FolderSystem
                activeFileReposityStructure={
                  activeMatterFileRepositoryStructure
                }
              />
            ) : (
              <div className="bg-card text-foreground mt-4 rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between border-b pb-2">
                  <h3 className="font-medium">Folders</h3>
                </div>
                <Text variant="subHeading" className="py-4">
                  Create the default matter file repository structure first.
                </Text>
              </div>
            )}
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter file repository structure?`}
        open={isDeleting && !!activeMatterFileRepositoryStructure}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeMatterFileRepositoryStructure) {
            await handleDelete();
          }
        }}
      />
    </>
  );
}
